<!--
  TinoActionQueue.vue — "Your Desk" action items widget.
  Static mock actions; ready to accept props from a future API.
-->
<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const URGENCY = {
  high:   { color: '#ef4444', bg: '#fef2f2', label: 'High' },
  medium: { color: '#f59e0b', bg: '#fffbeb', label: 'Medium' },
  low:    { color: '#22c55e', bg: '#f0fdf4', label: 'Low' },
}

const actions = computed(() => [
  {
    id: 1,
    icon: 'mdi-account-switch-outline',
    iconColor: '#f59e0b',
    iconBg: '#fffbeb',
    title: t('dashboard.tino.queue.transfers'),
    desc: '3 conversations require your review before being escalated to specialists.',
    urgency: 'high',
    cta: t('dashboard.tino.queue.review'),
  },
  {
    id: 2,
    icon: 'mdi-file-document-edit-outline',
    iconColor: '#6366f1',
    iconBg: '#eef2ff',
    title: t('dashboard.tino.queue.quotes'),
    desc: '5 quotes are waiting for approval before being sent to customers.',
    urgency: 'medium',
    cta: t('dashboard.tino.queue.approve'),
  },
  {
    id: 3,
    icon: 'mdi-robot-outline',
    iconColor: '#0ea5e9',
    iconBg: '#f0f9ff',
    title: t('dashboard.tino.queue.training'),
    desc: 'Tino identified 8 new intents that need labelling to improve accuracy.',
    urgency: 'medium',
    cta: t('dashboard.tino.queue.label'),
  },
  {
    id: 4,
    icon: 'mdi-alert-circle-outline',
    iconColor: '#ef4444',
    iconBg: '#fef2f2',
    title: t('dashboard.tino.queue.errors'),
    desc: '2 integrations returned errors during the last sync. Attention needed.',
    urgency: 'high',
    cta: t('dashboard.tino.queue.fix'),
  },
])
</script>

<template>
  <VCard class="tino-queue-card" flat>
    <!-- Header -->
    <div class="tino-queue-header">
      <div class="d-flex align-center gap-2">
        <VIcon icon="mdi-inbox-outline" size="18" color="primary" />
        <span class="text-sm font-weight-semibold">{{ t('dashboard.tino.queue.title') }}</span>
      </div>
      <VChip size="x-small" color="error" variant="flat" class="text-xs">
        {{ actions.length }} {{ t('dashboard.tino.queue.pending') }}
      </VChip>
    </div>

    <VDivider />

    <!-- Action list -->
    <div class="tino-queue-list">
      <div
        v-for="(action, idx) in actions"
        :key="action.id"
        class="tino-queue-item"
        :class="{ 'has-border': idx < actions.length - 1 }"
      >
        <!-- Icon -->
        <div
          class="tino-queue-icon"
          :style="{ background: action.iconBg }"
        >
          <VIcon :icon="action.icon" size="18" :color="action.iconColor" />
        </div>

        <!-- Text -->
        <div class="flex-grow-1 min-w-0">
          <div class="d-flex align-center gap-2 mb-1">
            <p class="text-xs font-weight-semibold text-high-emphasis mb-0">{{ action.title }}</p>
            <VChip
              size="x-small"
              variant="flat"
              :style="{
                background: URGENCY[action.urgency]?.bg,
                color: URGENCY[action.urgency]?.color,
              }"
            >
              {{ URGENCY[action.urgency]?.label }}
            </VChip>
          </div>
          <p class="text-xs text-medium-emphasis mb-2 tino-desc">{{ action.desc }}</p>
          <VBtn size="x-small" variant="outlined" color="primary">{{ action.cta }}</VBtn>
        </div>
      </div>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.tino-queue-card {
  border: 1px solid rgba(var(--v-border-color), 0.1);
  border-radius: 16px !important;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
  block-size: 100%;
}

.tino-queue-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
}

.tino-queue-list {
  padding: 8px 0;
}

.tino-queue-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 20px;
  transition: background 0.15s;

  &.has-border {
    border-block-end: 1px solid rgba(var(--v-border-color), 0.08);
  }

  &:hover {
    background: rgba(var(--v-theme-primary), 0.03);
  }
}

.tino-queue-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: 36px;
  block-size: 36px;
  border-radius: 10px;
  flex-shrink: 0;
}

.tino-desc {
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
